import { classifyPaymentCode, ScannedPaymentCode } from '@/lib/financial/payment-code'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MdClose, MdFlashlightOff, MdFlashlightOn, MdKeyboard } from 'react-icons/md'

export type PaymentCodeKind = 'pix' | 'boleto'
export type DetectedPaymentCode = Exclude<ScannedPaymentCode, { kind: 'unknown' }>

interface Detector {
    detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string }>>
}

const FORMATS = ['qr_code', 'itf']
const SCAN_INTERVAL_MS = 120

/**
 * Usa o BarcodeDetector nativo quando o navegador lê QR e ITF (Chrome no Android
 * e no desktop). Nos demais (Safari no iOS, Firefox) cai para o zxing em wasm,
 * carregado só agora e servido pelo nosso próprio build, não por CDN.
 */
async function createDetector(): Promise<Detector> {
    const Native = (window as any).BarcodeDetector
    if (Native?.getSupportedFormats) {
        try {
            const supported: string[] = await Native.getSupportedFormats()
            if (FORMATS.every(format => supported.includes(format))) return new Native({ formats: FORMATS })
        } catch {
            // segue para o zxing
        }
    }
    const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
        import('barcode-detector/ponyfill'),
        import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ])
    prepareZXingModule({
        overrides: {
            locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path),
        },
    })
    return new BarcodeDetector({ formats: ['qr_code', 'itf'] })
}

function cameraErrorMessage(error: any): string {
    if (!window.isSecureContext) return 'A câmera só funciona em conexão segura (HTTPS).'
    switch (error?.name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return 'O acesso à câmera foi negado. Libere a permissão nas configurações do navegador e tente de novo.'
        case 'NotFoundError':
        case 'OverconstrainedError':
            return 'Nenhuma câmera foi encontrada neste aparelho.'
        case 'NotReadableError':
            return 'A câmera está sendo usada por outro aplicativo.'
        default:
            return 'Não foi possível abrir a câmera.'
    }
}

const TITLES: Record<string, string> = {
    'pix,boleto': 'Pagar com a câmera',
    boleto: 'Ler código de barras',
    pix: 'Ler QR Code Pix',
}

const WRONG_KIND_NOTICE: Record<PaymentCodeKind, string> = {
    pix: 'Este é um QR Code Pix. Use a opção Enviar PIX para pagá-lo.',
    boleto: 'Este é o código de barras de um boleto. Use a opção Pagar Conta.',
}

interface PaymentCodeScannerProps {
    accept?: PaymentCodeKind[]
    onDetected: (code: DetectedPaymentCode) => void
    onClose: () => void
}

export default function PaymentCodeScanner({ accept = ['pix', 'boleto'], onDetected, onClose }: PaymentCodeScannerProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const doneRef = useRef(false)
    // Boleto só é aceito quando a mesma linha sai em duas leituras seguidas:
    // o ITF lido pela câmera às vezes troca um dígito e ainda assim fecha o DV.
    const lastBoletoRef = useRef<string | null>(null)
    const noticeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
    const onDetectedRef = useRef(onDetected)
    onDetectedRef.current = onDetected

    const [status, setStatus] = useState<'starting' | 'scanning' | 'error'>('starting')
    const [errorMessage, setErrorMessage] = useState('')
    const [notice, setNotice] = useState('')
    const [torchAvailable, setTorchAvailable] = useState(false)
    const [torchOn, setTorchOn] = useState(false)

    const acceptKey = [...accept].sort((a, b) => b.localeCompare(a)).join(',')
    const acceptsBoleto = accept.includes('boleto')
    const acceptsPix = accept.includes('pix')

    const showNotice = useCallback((message: string) => {
        setNotice(message)
        clearTimeout(noticeTimerRef.current)
        noticeTimerRef.current = setTimeout(() => setNotice(''), 3000)
    }, [])

    const handleRawValue = useCallback(
        (raw: string) => {
            const code = classifyPaymentCode(raw)
            if (code.kind === 'unknown') {
                showNotice('Código não reconhecido como Pix ou boleto.')
                return
            }
            if (!accept.includes(code.kind)) {
                showNotice(WRONG_KIND_NOTICE[code.kind])
                return
            }
            if (code.kind === 'boleto' && lastBoletoRef.current !== code.digitableLine) {
                lastBoletoRef.current = code.digitableLine
                return
            }
            doneRef.current = true
            navigator.vibrate?.(80)
            onDetectedRef.current(code)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [acceptKey, showNotice],
    )

    useEffect(() => {
        let cancelled = false
        let timer: ReturnType<typeof setTimeout> | undefined

        const start = async () => {
            if (!navigator.mediaDevices?.getUserMedia) {
                setErrorMessage(cameraErrorMessage(null))
                setStatus('error')
                return
            }
            try {
                // Resolução alta: o código de barras do boleto tem barras finas e
                // não fecha a leitura em 640x480.
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1920 },
                        height: { ideal: 1080 },
                    },
                })
                if (cancelled) {
                    stream.getTracks().forEach(track => track.stop())
                    return
                }
                streamRef.current = stream
                const track = stream.getVideoTracks()[0]
                const capabilities: any = track.getCapabilities?.() ?? {}
                setTorchAvailable(Boolean(capabilities.torch))
                if (Array.isArray(capabilities.focusMode) && capabilities.focusMode.includes('continuous')) {
                    await track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] }).catch(() => undefined)
                }

                const video = videoRef.current!
                video.srcObject = stream
                await video.play()

                const detector = await createDetector()
                if (cancelled) return
                setStatus('scanning')

                const tick = async () => {
                    if (cancelled || doneRef.current) return
                    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                        try {
                            const results = await detector.detect(video)
                            for (const result of results) {
                                if (cancelled || doneRef.current) return
                                if (result.rawValue) handleRawValue(result.rawValue)
                            }
                        } catch {
                            // quadro ilegível: tenta o próximo
                        }
                    }
                    if (!cancelled && !doneRef.current) timer = setTimeout(tick, SCAN_INTERVAL_MS)
                }
                tick()
            } catch (error) {
                if (cancelled) return
                setErrorMessage(cameraErrorMessage(error))
                setStatus('error')
            }
        }

        start()

        return () => {
            cancelled = true
            clearTimeout(timer)
            clearTimeout(noticeTimerRef.current)
            streamRef.current?.getTracks().forEach(track => track.stop())
            streamRef.current = null
        }
    }, [handleRawValue])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    const toggleTorch = async () => {
        const track = streamRef.current?.getVideoTracks()[0]
        if (!track) return
        const next = !torchOn
        try {
            await track.applyConstraints({ advanced: [{ torch: next } as any] })
            setTorchOn(next)
        } catch {
            setTorchAvailable(false)
        }
    }

    const hint = acceptsBoleto && acceptsPix
        ? 'Aponte para o QR Code Pix ou para o código de barras do boleto.'
        : acceptsBoleto
            ? 'Deixe o código de barras na horizontal, ocupando a largura da moldura.'
            : 'Centralize o QR Code Pix na moldura.'

    const frameClass = acceptsBoleto && acceptsPix
        ? 'w-[88%] max-w-lg aspect-[16/10]'
        : acceptsBoleto
            ? 'w-[92%] max-w-xl aspect-[3/1]'
            : 'w-[70%] max-w-xs aspect-square'

    return createPortal(
        <div className="fixed inset-0 z-[1000] bg-black text-white flex flex-col" role="dialog" aria-modal="true">
            <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />

            <div className="relative z-10 flex items-center justify-between px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-3 bg-gradient-to-b from-black/70 to-transparent">
                <h3 className="font-semibold text-base">{TITLES[acceptKey] ?? TITLES['pix,boleto']}</h3>
                <button type="button" className="btn btn-circle btn-sm btn-ghost text-white" onClick={onClose} aria-label="Fechar">
                    <MdClose className="text-xl" />
                </button>
            </div>

            <div className="relative z-10 flex-1 flex items-center justify-center px-4">
                {status === 'error' ? (
                    <div className="max-w-sm text-center bg-black/70 rounded-2xl p-6 space-y-4">
                        <p className="text-sm leading-relaxed">{errorMessage}</p>
                        <button type="button" className="btn btn-sm" onClick={onClose}>
                            <MdKeyboard /> Digitar o código
                        </button>
                    </div>
                ) : (
                    <div className={`relative ${frameClass} rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]`}>
                        <span className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-2xl" />
                        <span className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-2xl" />
                        <span className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-2xl" />
                        <span className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-2xl" />
                        {status === 'scanning' && (
                            <span className="absolute left-3 right-3 top-1/2 h-0.5 bg-rose-500/90 shadow-[0_0_8px_2px_rgba(244,63,94,0.6)] animate-pulse" />
                        )}
                        {status === 'starting' && (
                            <span className="absolute inset-0 flex items-center justify-center">
                                <span className="loading loading-spinner loading-md" />
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="relative z-10 px-4 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-black/80 to-transparent flex flex-col items-center gap-3">
                <p className="text-sm text-white/85 text-center max-w-sm min-h-[2.5rem]">{notice || hint}</p>
                <div className="flex items-center gap-3">
                    {torchAvailable && (
                        <button type="button" className="btn btn-sm bg-white/15 border-white/20 text-white hover:bg-white/25" onClick={toggleTorch}>
                            {torchOn ? <MdFlashlightOff /> : <MdFlashlightOn />}
                            {torchOn ? 'Desligar lanterna' : 'Lanterna'}
                        </button>
                    )}
                    {status !== 'error' && (
                        <button type="button" className="btn btn-sm bg-white/15 border-white/20 text-white hover:bg-white/25" onClick={onClose}>
                            <MdKeyboard /> Digitar o código
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    )
}
